/**
 * COM-63 — which payload `BasketProvider` derives its basket from, as a pure
 * function so the precedence is testable without rendering the provider.
 *
 * Inputs, in precedence order:
 *  - `fetcherData` — the shared keyed "basket" fetcher. An object is the
 *    freshest truth (loader OR action response). `null` is a CONTAINED
 *    clientLoader failure (the reload was answered, and the answer was "no");
 *    it can linger on the fetcher across later mutations. `undefined` is
 *    "never populated / awaiting".
 *  - `lastGoodKeyed` — the last loader-shaped keyed payload we saw. Preferred
 *    over `softBasket` after a failed reload: `softBasket` is the one-shot
 *    MOUNT fetch and predates every mutation, so falling back to it drops the
 *    header count to the page-load value.
 *  - `softBasket` — the fail-soft mount hydrate; only backs the
 *    pre-interaction window.
 *  - `replaySnapshot` — the snapshot the provider already handed to consumers,
 *    replayed ONLY while `awaiting` AND `replayArmed` (an SFL move-to-cart /
 *    add-back, or any mutation made on /cart). Replaying it globally would paint the
 *    pre-mutation basket during every ATC — exactly what the SFCC-11878/11869
 *    suppression exists to prevent (stale header count, stale GA4 view_cart).
 *  - `hasOptimisticSnap` — a same-page remove / save-for-later snap is on
 *    screen. It renders the correct view on top of `softBasket`, so the
 *    awaiting suppression is skipped: blanking there just flashes the cart
 *    skeleton on the first cold-cart interaction (ET-204 follow-up). LATCHED
 *    by the caller for the whole await, not read live off the snap map — see
 *    `BasketProvider`'s `snapLatchRef`.
 */
export type SelectBasketRawInput<Raw, Snapshot> = {
  fetcherData: Raw | null | undefined;
  softBasket: Raw | undefined;
  lastGoodKeyed: Raw | undefined;
  awaiting: boolean;
  replayArmed: boolean;
  replaySnapshot: Snapshot | undefined;
  hasOptimisticSnap: boolean;
};

export type SelectBasketRawResult<Raw, Snapshot> =
  | { kind: "raw"; raw: Raw }
  | { kind: "replay"; snapshot: Snapshot }
  | { kind: "none" };

/**
 * COM-294: whether the basket mutation in flight (or awaiting its refresh) started on
 * /cart, latched at its first render. `null` when none. An add begun on a PDP must not
 * replay its pre-add basket when the shopper reaches /cart before the refresh lands.
 */
export function mutationStartedOnCart(
  prev: boolean | null,
  {
    inFlight,
    awaiting,
    onCart,
  }: { inFlight: boolean; awaiting: boolean; onCart: boolean },
): boolean | null {
  if (!inFlight && !awaiting) return null;
  return prev ?? (inFlight ? onCart : null);
}

/** Loader responses carry `products`; action responses don't. */
export function isLoaderShaped(data: unknown): boolean {
  return data != null && typeof data === "object" && "products" in data;
}

export function selectBasketRaw<Raw, Snapshot>({
  fetcherData,
  softBasket,
  lastGoodKeyed,
  awaiting,
  replayArmed,
  replaySnapshot,
  hasOptimisticSnap,
}: SelectBasketRawInput<Raw, Snapshot>): SelectBasketRawResult<Raw, Snapshot> {
  if (fetcherData) return { kind: "raw", raw: fetcherData };

  // The P2-7b replay decision comes FIRST and treats `null` exactly like
  // `undefined`: a contained reload failure (or a stale null left over from
  // an earlier one) must not unmount the cart body mid-move — the
  // `useSflMoveToCart` cleanup timer and the RemovedItemCard settle observer
  // live inside it (review round 2, WARN-2).
  if (awaiting && replayArmed && replaySnapshot) {
    return { kind: "replay", snapshot: replaySnapshot };
  }

  if (fetcherData === null) {
    // Contained reload failure. Prefer the last good KEYED payload over the
    // mount-time snapshot — even while awaiting: the refresh hook bounds the
    // wait (attempt cap + valve), and "none" here would swap the whole cart
    // to a skeleton on every later mutation while the null lingers.
    const fallback = lastGoodKeyed ?? softBasket;
    return fallback ? { kind: "raw", raw: fallback } : { kind: "none" };
  }

  // Awaiting a post-mutation refresh with nothing keyed yet: suppress the
  // pre-mutation `softBasket` (=> loading state) — the SFCC-11878/11869
  // contract for an ordinary ATC / auth flip — unless an optimistic snap is
  // overlaying it with the correct view.
  if (awaiting && !hasOptimisticSnap) return { kind: "none" };

  return softBasket ? { kind: "raw", raw: softBasket } : { kind: "none" };
}
